import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { styled } from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";
import { Link as RouterLink } from "react-router-dom";

import { useProfileContext } from "contexts/ProfileContext";
import { DARK_BLUE } from "theme/colors";
import { userIsPartner } from "utils/profile/profileHelper";

export type Breadcrumb = {
  label: string;
  href: string;
};

type Props = {
  additionalBreadcrumbs?: Breadcrumb[];
};

export const createBreadCrumb = (label: string, href: string): Breadcrumb => ({ label, href });

export const ViewBreadcrumbs = ({ additionalBreadcrumbs }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { profile } = useProfileContext();
  const isPartner = userIsPartner(profile);
  const pathnames = location.pathname.split("/").filter(x => x);

  const isDisabledForPartner = (to: string) => isPartner && to === "/patients";

  const breadcrumbNameMap: { [key: string]: string } = {
    "/admin": t("views.admin"),
    "/calendar": t("views.calendar"),
    "/calendar/services": t("views.services"),
    "/dashboard": t("views.dashboard"),
    "/invitations": t("views.invitations"),
    "/library/your-library": t("views.library"),
    "/messages": t("views.messages"),
    "/patients": t("views.patients"),
    "/settings": t("views.settings"),
  };

  const renderBreadcrumb = (label: string, to: string, isLast: boolean) => {
    if (isLast) {
      return (
        <Typography key={`${label}-${to}`} variant="subtitle1" color={DARK_BLUE[60]}>
          {label}
        </Typography>
      );
    }

    if (isDisabledForPartner(to)) {
      return (
        <Typography key={`${label}-${to}`} variant="subtitle1">
          {label}
        </Typography>
      );
    }

    return (
      <Typography key={`${label}-${to}`} variant="subtitle1">
        <Link underline="hover" sx={{ fontSize: "inherit", color: "inherit" }} component={RouterLink} to={to}>
          {label}
        </Link>
      </Typography>
    );
  };

  return (
    <StyledBreadcrumbs aria-label="breadcrumb" separator={<NavigateNextIcon sx={{ fontSize: "1em" }} />}>
      {renderBreadcrumb(t("views.home"), "/dashboard", pathnames.length === 0)}

      {pathnames
        .map((value, index) => {
          const to = `/${pathnames.slice(0, index + 1).join("/")}`;

          if (!breadcrumbNameMap[to]) {
            return null;
          }

          return { to, label: breadcrumbNameMap[to] };
        })
        .filter(breadcrumb => breadcrumb !== null)
        .map((breadcrumb, index, array) => {
          // Ensure no duplicate labels
          if (breadcrumb === null || (index > 0 && array[index - 1]?.label === breadcrumb.label)) {
            return null;
          }
          const isLast = index === array.length - 1 && !additionalBreadcrumbs?.length;
          return renderBreadcrumb(breadcrumb.label, breadcrumb.to, isLast);
        })}

      {additionalBreadcrumbs &&
        additionalBreadcrumbs.map((breadcrumb, index) => {
          const isLast = index === additionalBreadcrumbs.length - 1;
          return renderBreadcrumb(breadcrumb.label, breadcrumb.href, isLast);
        })}
    </StyledBreadcrumbs>
  );
};

const StyledBreadcrumbs = styled(Breadcrumbs)({
  "& .MuiBreadcrumbs-separator": {
    marginLeft: 4,
    marginRight: 4,
    marginBottom: 1,
  },
});
